import { createRouter, createWebHistory } from 'vue-router';
import { useAccountStore } from '@stores/account.store';
import { useFanStore } from '@stores/fan.store';

import Login from '@views/Login.vue';
import SignUp from '@views/SignUp.vue';
import ForgotPassword from '@views/ForgotPassword.vue';
import ResetPassword from '@views/ResetPassword.vue';
import Profile from '@views/Profile.vue';
import Home from '@views/Home.vue';
import Fanzone from '@views/Fanzone/index.vue';
import Post from '@views/Fanzone/id.vue';
import MagicLink from '@views/MagicLink.vue';
import ContentPreview from '@views/ContentPreview.vue';
import ChangeEmail from '@components/views/ChangeEmail.vue';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Public routes
    {
      path: '/preview',
      name: 'Preview',
      component: ContentPreview,
      meta: {
        noHeader: true,
        public: true,
        preview: true,
        noFooter: true,
        noPhoto: true,
      },
    },
    {
      path: '/change-email',
      name: 'ChangeEmail',
      component: ChangeEmail,
      meta: {
        public: true,
      },
      beforeEnter: (to, from, next) => {
        if (!to.query.token) {
          next({ name: from?.name || 'Home' });
        } else {
          const accountStore = useAccountStore();
          accountStore.logoutFan(false);
          next();
        }
      },
    },
    // Auth routes
    {
      path: '/signup',
      name: 'Signup',
      component: SignUp,
    },
    {
      path: '/login',
      name: 'Login',
      component: Login,
    },
    {
      path: '/forgot-password',
      name: 'ForgotPassword',
      component: ForgotPassword,
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: ResetPassword,
      beforeEnter: (to, from, next) => {
        if (!to.query.token) {
          next({ name: 'Login' });
        } else {
          next();
        }
      },
    },
    {
      path: '/magic-link',
      name: 'MagicLink',
      component: MagicLink,
    },
    // Protected routes
    {
      path: '/',
      name: 'Home',
      component: Home,
      meta: {
        noFooter: true,
        noPhoto: true,
        requiresAuth: true,
      },
    },
    {
      path: '/fanzone',
      name: 'Fanzone',
      meta: {
        requiresAuth: true,
        noPhoto: true,
        lightBg: true,
      },
      children: [
        {
          path: '',
          name: 'FanzoneIndex',
          meta: {
            noPhoto: true,
            noFooter: true,
          },
          component: Fanzone,
        },
        {
          path: ':id',
          name: 'FanzonePost',
          meta: {
            noFooter: true,
          },
          component: Post,
        },
      ],
    },
    {
      path: '/profile',
      name: 'Profile',
      component: Profile,
      meta: {
        noFooter: true,
        requiresAuth: true,
        lightBg: true,
      },
    },
    // Catch all route - must be last - must use '/' as the path syntax to discard params
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
});

// Navigation guard to check authentication
router.beforeEach(async (to, from, next) => {
  const accountStore = useAccountStore();
  const fanStore = useFanStore();
  accountStore.readAuthToken();
  // Handle email parameter
  if (to.query.email) {
    const email = decodeURIComponent((to.query.email as string)?.replace(/\s|[%]20/g, '%2B'));
    accountStore.guestEmail = email || '';
    const { email: _, ...queryWithoutEmail } = to.query;
    next({ ...to, query: queryWithoutEmail });
    return; // NOTE return, so we don't intercept interrupted path
  }

  if (to.meta.requiresAuth && !accountStore.isAuthenticated) {
    try {
      sessionStorage.setItem('interruptedPath', to.fullPath);
    } catch (error) {
      console.warn('Failed to store interrupted path:', error);
    }
    next({ name: 'Login' });
  } else if (to.meta.requiresAuth && accountStore.isAuthenticated) {
    try {
      const interruptedPath = sessionStorage.getItem('interruptedPath');
      if (interruptedPath) {
        next(interruptedPath);
        sessionStorage.removeItem('interruptedPath');
      } else {
        if (!fanStore.fanId) fanStore.fanGet();
        next();
      }
    } catch (error) {
      console.warn('Failed to retrieve interrupted path:', error);
      if (!fanStore.fanId) fanStore.fanGet();
      next();
    }
  } else if (!to.meta.requiresAuth && !to.meta.public && accountStore.isAuthenticated) {
    // make sure to remove the token from the url
    const { token: _, ...queryWithoutToken } = to.query;
    next({ name: from?.name || 'Home', query: queryWithoutToken });
  } else {
    if (to.meta.requiresAuth && accountStore.isAuthenticated && !fanStore.fanId) fanStore.fanGet();
    next();
  }
});

export default router;
